import { ReactElement, useState } from "react"
import { useNavigate } from "react-router-dom"
import BaseTable, { AutoResizer, Column } from "react-base-table"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCloud,
  faEye,
  faFileExcel,
  faPen,
  faSquareCaretDown,
  faTrash,
  IconDefinition,
} from "@fortawesome/free-solid-svg-icons"
import { Button, ContextMenu, ContextMenuItem, Loading, Modal, Pagination, Tag } from "@wfp/ui"
import { iconAddOutline } from "@wfp/icons"

import { useDeleteMSMutation, useGetMSByIdQuery, useGetMSQuery } from "services/marketprices/mappingSchemas"

import { MARKET_MODULE } from "constants/constants"
import { MappingSchemaData, SimpleSelect } from "interfaces/dataInterfaces"

import CountrySelector from "components/Shared/HeaderFilters/AssociatedCountriesSelector"
import HeaderFilters from "components/Shared/HeaderFilters/HeaderFilters"
import SearchBox from "components/Shared/HeaderFilters/SearchBox"
import PermissionWrapper from "components/Shared/Common/PermissionWrapper"
import NewDataContent from "../../components/Markets/Dashboard/NewDataContent"
import DetailsModal from "./DetailsModal"

import { formatDate } from "../../utils/helpers"
import { MappingType } from "../../constants/markets"

const tagType = {
  1: "warning",
  2: "error",
  4: "success",
}

const status = {
  1: "In Progress",
  2: "Invalid",
  4: "Complete",
}

const permissionsProps = {
  moduleId: MARKET_MODULE,
  subModule: "mappingSchema",
}

const MappingSchemaPage = (): ReactElement => {
  const navigate = useNavigate()

  const [page, setPage] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(20)
  const [showModal, setShowModal] = useState<boolean>(false)

  const [country, setCountry] = useState<undefined | SimpleSelect>()
  const [query, setQuery] = useState<string | undefined>("")
  const [actionItem, setActionItem] = useState<{ action: "delete" | "details"; data: MappingSchemaData } | null>(null)

  const { data: mappingSchemas, isFetching } = useGetMSQuery({
    adm0code: country?.value,
    skip: page === 1 ? 0 : (page - 1) * pageSize,
    top: pageSize,
    query,
  })
  const { data: msData, isFetching: loadingMsData } = useGetMSByIdQuery(actionItem?.data.mappingSchemaId ?? 0, {
    skip: !actionItem || actionItem.action === "delete",
    refetchOnMountOrArgChange: true,
  })
  const [deleteMS] = useDeleteMSMutation()

  const handleCountry = (item?: SimpleSelect): void => {
    setCountry(item)
    setPage(1)
  }

  const handleSearch = (value: string): void => {
    setQuery(value)
    setPage(1)
  }

  const handleDelete = async (): Promise<void> => {
    if (!actionItem?.data?.mappingSchemaId) return
    await deleteMS(actionItem.data.mappingSchemaId).unwrap()
    setActionItem(null)
  }

  const RenderContextItem = ({
    onAction,
    feature,
    label,
    icon,
    color,
    disabled = false,
  }: {
    onAction: () => void
    feature: string
    label: string
    icon: IconDefinition
    color: string
    disabled?: boolean
  }): ReactElement | null => {
    if (disabled) return null
    return (
      <PermissionWrapper {...permissionsProps} feature={feature}>
        <ContextMenuItem className="p-1.5 px-2 text-left w-40 hover:bg-slate-200" onClick={onAction}>
          <div>
            <FontAwesomeIcon icon={icon} color={color} className="mr-1" />
            <span style={{ color }}>{label}</span>
          </div>
        </ContextMenuItem>
      </PermissionWrapper>
    )
  }

  const columns = [
    {
      key: "mappingSchemaId",
      dataKey: "mappingSchemaId",
      title: "ID",
      width: 130,
    },
    {
      key: "type",
      title: "",
      align: Column.Alignment.CENTER,
      width: 24,
      maxWidth: 24,
      flexGrow: 0,
      cellRenderer: ({ rowData }) =>
        rowData.mappingSchemaType?.toLowerCase() === "excel" ? (
          <FontAwesomeIcon icon={faFileExcel} size="lg" color="green" title="Excel" className="h-4 w-4" />
        ) : (
          <FontAwesomeIcon icon={faCloud} size="lg" color="var(--interactive-01)" title="MoDA" className="h-4 w-4" />
        ),
    },
    {
      key: "mappingSchemaStatusId",
      dataKey: "mappingSchemaStatusId",
      title: "Status",
      width: 120,
      minWidth: 120,
      cellRenderer: ({ rowData }) => (
        <Tag type={tagType[rowData.mappingSchemaStatusId] || "info"} className="font-bold">
          <span className="capitalize">{status[rowData.mappingSchemaStatusId]}</span>
        </Tag>
      ),
    },
    {
      key: "countryName",
      dataKey: "countryName",
      title: "Country",
      width: 200,
      flexGrow: 1,
    },
    {
      key: "mappingSchemaName",
      dataKey: "mappingSchemaName",
      title: "Schema Name",
      width: 300,
      flexGrow: 2,
    },
    {
      key: "lastUpdatedDate",
      dataKey: "lastUpdatedDate",
      title: "Last Update",
      width: 200,
      cellRenderer: ({ rowData }) => formatDate(rowData.lastUpdatedDate || "", "DD-MMMM-YYYY HH:mm"),
    },
    {
      key: "action",
      width: 60,
      align: Column.Alignment.CENTER,
      maxWidth: 60,
      flexGrow: 0,
      cellRenderer: ({ rowData }: { rowData: MappingSchemaData }) => (
        <div
          aria-hidden
          className="d-flex align-items-center justify-end"
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          <ContextMenu
            content={
              <>
                <RenderContextItem
                  label="Details"
                  icon={faEye}
                  color="var(--interactive-01)"
                  feature="details"
                  onAction={() => setActionItem({ data: rowData, action: "details" })}
                />
                <RenderContextItem
                  label="Edit"
                  icon={faPen}
                  color="var(--support-02)"
                  feature="edit"
                  onAction={() =>
                    rowData.mappingSchemaType?.toLowerCase() === "excel"
                      ? navigate(`edit/${rowData.mappingSchemaId}`)
                      : navigate(`/market-prices/moda-data-upload/edit/${rowData.mappingSchemaId}`)
                  }
                />
                <RenderContextItem
                  label="Delete"
                  icon={faTrash}
                  color="var(--support-01)"
                  feature="delete"
                  onAction={() => setActionItem({ data: rowData, action: "delete" })}
                />
              </>
            }
          >
            <div className="cursor-pointer">
              <FontAwesomeIcon icon={faSquareCaretDown} color="var(--interactive-01)" size="xl" />
            </div>
          </ContextMenu>
        </div>
      ),
    },
  ]

  return (
    <>
      <HeaderFilters
        leftWidth="w-3/4"
        left={
          <div className="flex gap-x-4 items-center w-full mr-4">
            <CountrySelector onSelect={handleCountry} className="w-full" />
            <SearchBox onSubmit={handleSearch} />
          </div>
        }
        right={
          <PermissionWrapper {...permissionsProps} feature="addNew">
            <Button onClick={() => setShowModal(true)} icon={iconAddOutline} iconReverse>
              Add New Mapping Schema
            </Button>
          </PermissionWrapper>
        }
      />

      <div
        className={isFetching ? "mt-2 d-flex align-center justify-center" : "mt-2"}
        style={{ height: "calc(100vh - 240px)", minHeight: "60vh" }}
      >
        {isFetching ? (
          <Loading withOverlay={false} small />
        ) : (
          <AutoResizer>
            {({ width, height }) => (
              <BaseTable
                data={mappingSchemas?.items ?? []}
                loading={isFetching}
                width={width}
                height={height}
                rowKey="mappingSchemaId"
                className="custom-table"
                rowHeight={38}
                headerHeight={45}
                columns={columns}
                emptyRenderer={() => <p>No records to display</p>}
              />
            )}
          </AutoResizer>
        )}
      </div>

      <Modal
        open={actionItem?.action === "delete"}
        modalHeading="Confirm Delete"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleDelete}
        onRequestClose={() => setActionItem(null)}
        danger
      >
        Are you sure you want to delete <b>{actionItem?.data.mappingSchemaName ?? "this mapping schema"}</b>?
      </Modal>

      {actionItem?.data && msData && (
        <DetailsModal
          onClose={() => setActionItem(null)}
          data={msData}
          isOpen={actionItem.action === "details"}
          isLoading={loadingMsData}
        />
      )}

      <Pagination
        totalItems={mappingSchemas?.count ?? 0}
        pageSize={pageSize}
        pageSizes={[20, 50, 100]}
        page={page}
        onChange={event => {
          const item = event as unknown as { page: number; pageSize: number }
          setPage(item.page)
          setPageSize(item.pageSize)
        }}
      />

      <Modal
        open={showModal}
        onRequestClose={() => setShowModal(false)}
        modalLabel="Add New Mapping Schema"
        passiveModal
        lazyLoad
      >
        <NewDataContent moda={MappingType.moda} excel={MappingType.excel} />
      </Modal>
    </>
  )
}

export default MappingSchemaPage
